import apiClient from "../apiClient";

const resolveResponse = (data) => {
  let result = data && data.$values ? data.$values : data;
  if (!Array.isArray(result)) return [];
  const idToObjectMap = {};
  const traverse = (obj) => {
    if (Array.isArray(obj)) {
      obj.forEach((item) => traverse(item));
    } else if (obj && typeof obj === 'object') {
      if (obj.$id) {
        idToObjectMap[obj.$id] = obj;
      }
      Object.values(obj).forEach(traverse);
    }
  };
  traverse(data);
  return result.map((item) => (item.$ref ? idToObjectMap[item.$ref] : item));
};

export const getPartners = async (query, instance, t) => {
  const client = await apiClient(instance);
  const response = await client.get('/api/Partner/getAllPartners', { params: { searchQuery: query } });
  let partnersData = resolveResponse(response.data);
  if (partnersData && partnersData.$values) {
    partnersData = partnersData.$values;
  }
  if (!Array.isArray(partnersData)) throw new Error(t('partnerManagementPage.generateCodeFailed'));
  return partnersData.map((partner) => ({
    id: partner.id,
    name: partner.name,
    description: partner.description,
    status: Number(partner.status), // Ensure status is a number
    deactivatedAt: partner.deactivatedAt,
    deactivationReason: partner.deactivationReason
  }));
};

export const createPartner = async (data, instance, t) => {
  const client = await apiClient(instance);
  return await client.post('/api/Partner', {
    name: data.name,
    description: data.description,
  });
};

export const editPartner = async (data, instance, t) => {
  const client = await apiClient(instance);
  
  return await client.put(`/api/Partner/${data.id}`, {
    name: data.name,
    description: data.description,
  });
};

export const assignPartnerAdmin = async ({ partnerId, email, assignedByEmail }, instance, t) => {
  const client = await apiClient(instance);
  return await client.post('/api/Partner/AssignPartnerAdminToPartner', {
    PartnerId: partnerId,
    Email: email,
    AssignedByEmail: assignedByEmail,
  });
};

export const unassignPartnerAdmin = async ({ partnerId, adminId }, instance, t) => {
  const client = await apiClient(instance);
  return await client.post(`/api/Partner/${partnerId}/UnassignUser`, {
    UserId: adminId,
  });
};

export const getPartnerAdmins = async (partnerId, instance, t) => {
  const client = await apiClient(instance);
  const response = await client.get(`/api/Partner/${partnerId}/admins`);
  let adminsData = response.data || [];
  if (adminsData.$values) adminsData = adminsData.$values;
  if (!Array.isArray(adminsData)) throw new Error(t('partnerManagementPage.fetchPartnerAdminsFailed'));
  return adminsData.map((admin) => ({
    id: admin.id,
    email: admin.email,
  }));
};

export const getAllPartnersForAdmin = async (instance) => {
  const client = await apiClient(instance);
  const response = await client.get('/api/Partner/getAllPartners');
  let partnersData = resolveResponse(response.data);
  if (!Array.isArray(partnersData)) throw new Error('Invalid partners data');
  return partnersData;
};

export const getUserPartners = async (instance) => {
  const client = await apiClient(instance);
  const response = await client.get('/api/Partner/userPartners');
  let partnersData = resolveResponse(response.data);
  if (!Array.isArray(partnersData)) throw new Error('Invalid partners data');
  return partnersData;
};

export const getPartnerStatistics = async (partnerId, instance) => {
  const client = await apiClient(instance);
  const response = await client.get(`/api/Partner/${partnerId}/statistics`);
  return response.data;
};

// Get a single partner basic info
export const getPartnerById = async (partnerId, instance) => {
  const client = await apiClient(instance);
  const response = await client.get(`/api/Partner/${partnerId}`);
  return response.data;
};

// Get partner details (extended settings) for completeness calculation
export const getPartnerDetails = async (partnerId, instance) => {
  const client = await apiClient(instance);
  try {
    const response = await client.get(`/api/Partner/${partnerId}/details`);
    return response.data || {};
  } catch (e) {
    return {}; // If 404 or error, treat as empty details
  }
};

export const deactivatePartner = async (partnerId, deactivationReason, instance) => {
  const client = await apiClient(instance);
  return await client.post(`/api/Partner/${partnerId}/deactivate`, {
    deactivationReason
  });
};

export const activatePartner = async (partnerId, instance) => {
  const client = await apiClient(instance);
  return await client.post(`/api/Partner/${partnerId}/activate`);
};

export const deletePartner = async (partnerId, instance) => {
  const client = await apiClient(instance);
  return await client.delete(`/api/Partner/${partnerId}`);
};

// Property Offers Management
export const getPartnerOffers = async (partnerId, instance) => {
  const client = await apiClient(instance);
  const response = await client.get(`/api/Partner/${partnerId}/offers`);
  let offersData = resolveResponse(response.data);
  if (!Array.isArray(offersData)) {
    // If it's not an array, it might be a single object or null
    return offersData ? [offersData] : [];
  }
  return offersData;
};

export const createPartnerOffer = async (partnerId, offerData, instance) => {
  const client = await apiClient(instance);
  return await client.post(`/api/Partner/${partnerId}/offers`, offerData);
};

export const updatePartnerOffer = async (partnerId, offerId, offerData, instance) => {
  const client = await apiClient(instance);
  return await client.put(`/api/Partner/${partnerId}/offers/${offerId}`, offerData);
};

export const deletePartnerOffer = async (partnerId, offerId, instance) => {
  const client = await apiClient(instance);
  return await client.delete(`/api/Partner/${partnerId}/offers/${offerId}`);
};
